/**
 * FeeRequestsPage — owner-approver / contractor_admin / super_admin
 * queue for additional-fee requests.
 *
 * Shows rows in 'requested' and 'completed_without_approval' status
 * (i.e. pending decisions). Each row exposes inline Approve / Deny /
 * Paid-at-normal-rate buttons. Deny + Paid-at-normal require a typed
 * reason — empty is blocked client-side AND server-side.
 *
 * Read-only history (approved / denied / paid_at_normal_clean_rate)
 * is not surfaced here yet — that's a reporting concern (#78).
 */

import { useState, type ReactNode } from 'react'
import { useTranslation }            from 'react-i18next'
import {
  useAdditionalFeeRequests,
  PENDING_QUEUE_STATUSES,
  type AdditionalFeeRequestRow,
}                                    from '@/hooks/useAdditionalFeeRequests'
import {
  useDecideAdditionalFee,
  type FeeDecision,
}                                    from '@/hooks/useDecideAdditionalFee'
import { useTenantFeatures }         from '@/hooks/useTenantFeatures'
import { isAppError }                from '@/lib/errors/AppError'

export function FeeRequestsPage(): ReactNode {
  const { t } = useTranslation()
  const { has: hasFeature, isLoading: flagsLoading } = useTenantFeatures()
  const featureEnabled = hasFeature('additional_fee_workflow')
  const { data, isLoading, error } = useAdditionalFeeRequests({ statuses: [...PENDING_QUEUE_STATUSES] })

  // Tenants without the exceedance-fee contract clause see a short
  // explanation instead of the queue. The hook still runs (calling a
  // hook conditionally would break the rules of hooks) but the body
  // never reads its data on the disabled branch.
  if (flagsLoading) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <p style={{ color: 'var(--brand-neutral-500)' }}>{t('feeRequests.loading', 'Loading queue…')}</p>
      </section>
    )
  }
  if (!featureEnabled) {
    return (
      <section
        data-testid="fee-requests-disabled"
        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
      >
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('feeRequests.title', 'Additional-fee requests')}
        </h1>
        <p style={{ margin: 0, color: 'var(--brand-neutral-500)' }}>
          {t('feeRequests.featureDisabled', 'Additional-fee requests are not enabled for this organization. Contact your admin to enable the workflow.')}
        </p>
      </section>
    )
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('feeRequests.title', 'Additional-fee requests')}
        </h1>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)' }}>
          {t('feeRequests.subtitle', 'Cleaner-filed exceedance requests awaiting your decision.')}
        </p>
      </header>

      {isLoading && (
        <p style={{ color: 'var(--brand-neutral-500)' }}>
          {t('feeRequests.loading', 'Loading queue…')}
        </p>
      )}
      {error && (
        <ErrorPanel message={error instanceof Error ? error.message : t('feeRequests.failedToLoad', 'Failed to load the queue.')} />
      )}

      {!isLoading && !error && (
        (data && data.length > 0)
          ? (
            <ul data-testid="fee-request-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {data.map((row) => (
                <FeeRequestRow key={row.id} row={row} />
              ))}
            </ul>
          )
          : (
            <p data-testid="fee-request-empty" style={{ color: 'var(--brand-neutral-500)' }}>
              {t('feeRequests.empty', 'No fee requests waiting for a decision right now.')}
            </p>
          )
      )}
    </section>
  )
}

function FeeRequestRow({ row }: { row: AdditionalFeeRequestRow }): ReactNode {
  const { t } = useTranslation()
  const decide = useDecideAdditionalFee()
  const [pendingDecision, setPendingDecision] = useState<FeeDecision | null>(null)
  const [reasonDraft, setReasonDraft]         = useState('')

  async function submit(decision: FeeDecision): Promise<void> {
    try {
      await decide.mutateAsync({
        requestId:    row.id,
        decision,
        denialReason: decision === 'approve' ? undefined : reasonDraft,
      })
      setPendingDecision(null)
      setReasonDraft('')
    } catch { /* error rendered via decide.error */ }
  }

  const errMsg = decide.error && isAppError(decide.error) ? decide.error.userMessage : null

  return (
    <li
      data-testid="fee-request-row"
      data-request-id={row.id}
      data-status={row.status}
      style={{
        background:   'var(--brand-white)',
        border:       '1px solid var(--brand-neutral-300)',
        borderRadius: 'var(--brand-radius-lg)',
        padding:      '0.85rem 1rem',
        display:      'flex',
        flexDirection:'column',
        gap:          '0.6rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div>
          <div data-testid="fee-request-amount" style={{ fontSize: '1.05rem', fontWeight: 600 }}>
            ${(row.requested_amount_cents / 100).toFixed(2)}
          </div>
          <div style={{ color: 'var(--brand-neutral-500)', fontSize: '0.8rem' }}>
            {row.status === 'completed_without_approval'
              ? t('feeRequests.statusCompletedWithoutApproval', 'Task already completed — owner decision still required')
              : t('feeRequests.statusRequested', 'Awaiting owner decision')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="fee-request-approve"
            onClick={() => { void submit('approve') }}
            disabled={decide.isPending}
            style={btnPrimary}
          >
            {t('feeRequests.approve', 'Approve')}
          </button>
          {row.status === 'requested' && (
            <button
              type="button"
              data-testid="fee-request-deny-open"
              onClick={() => setPendingDecision('deny')}
              disabled={decide.isPending}
              style={btnSecondary}
            >
              {t('feeRequests.deny', 'Deny')}
            </button>
          )}
          {row.status === 'completed_without_approval' && (
            <button
              type="button"
              data-testid="fee-request-paid-normal-open"
              onClick={() => setPendingDecision('paid_at_normal_clean_rate')}
              disabled={decide.isPending}
              style={btnSecondary}
            >
              {t('feeRequests.paidAtNormal', 'Paid at normal rate')}
            </button>
          )}
        </div>
      </div>

      <p data-testid="fee-request-reason" style={{ margin: 0, fontSize: '0.9rem', color: 'var(--brand-neutral-900)' }}>
        {row.reason}
      </p>

      {pendingDecision && (
        <div
          data-testid="fee-request-reason-form"
          data-decision={pendingDecision}
          style={{
            display:        'flex',
            flexDirection:  'column',
            gap:            '0.4rem',
            padding:        '0.6rem',
            background:     'var(--brand-neutral-50, var(--brand-neutral-100))',
            borderRadius:   'var(--brand-radius-sm)',
          }}
        >
          <label htmlFor={`reason-${row.id}`} style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>
            {t('feeRequests.reasonLabel', 'Reason (1–1000 chars)')}
          </label>
          <textarea
            id={`reason-${row.id}`}
            data-testid="fee-request-reason-input"
            value={reasonDraft}
            onChange={(e) => setReasonDraft(e.target.value.slice(0, 1000))}
            rows={3}
            maxLength={1000}
            style={{
              width:        '100%',
              fontSize:     '0.9rem',
              padding:      '0.4rem',
              borderRadius: 'var(--brand-radius-sm)',
              border:       '1px solid var(--brand-neutral-300)',
              resize:       'vertical',
            }}
          />
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              type="button"
              data-testid="fee-request-reason-submit"
              onClick={() => { void submit(pendingDecision) }}
              disabled={decide.isPending || reasonDraft.trim().length === 0}
              style={btnPrimary}
            >
              {t('feeRequests.confirm', 'Confirm')}
            </button>
            <button
              type="button"
              data-testid="fee-request-reason-cancel"
              onClick={() => { setPendingDecision(null); setReasonDraft('') }}
              style={btnSecondary}
            >
              {t('feeRequests.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}

      {errMsg && (
        <p role="alert" data-testid="fee-request-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', margin: 0 }}>
          {errMsg}
        </p>
      )}
    </li>
  )
}

const btnPrimary = {
  padding:      '0.4rem 0.8rem',
  background:   'var(--brand-primary)',
  color:        'var(--brand-white)',
  border:       'none',
  borderRadius: 'var(--brand-radius-sm)',
  fontSize:     '0.85rem',
  fontWeight:   600,
  cursor:       'pointer',
} as const

const btnSecondary = {
  padding:      '0.4rem 0.8rem',
  background:   'transparent',
  color:        'var(--brand-neutral-700)',
  border:       '1px solid var(--brand-neutral-300)',
  borderRadius: 'var(--brand-radius-sm)',
  fontSize:     '0.85rem',
  cursor:       'pointer',
} as const

function ErrorPanel({ message }: { message: string }): ReactNode {
  return (
    <div
      role="alert"
      style={{
        background:   'var(--brand-danger-subtle)',
        color:        'var(--brand-danger)',
        border:       '1px solid var(--brand-danger)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.75rem 1rem',
        fontSize:     '0.9rem',
      }}
    >
      {message}
    </div>
  )
}
